/**
 * Clock helpers shared by the timer bar and the task-detail timer buttons.
 *
 * Elapsed time is always RECOMPUTED from the timestamps, never accumulated in a
 * counter: a counter drifts under React StrictMode's double mount and under the
 * browser's throttling of inactive tabs.
 */
import type { ActiveTimer } from './timersApi';

/**
 * Seconds worked so far. `nowMs` should already include the server-clock
 * offset, otherwise a laptop whose clock lags shows negative time.
 */
export function elapsedSeconds(
  timer: Pick<ActiveTimer, 'runningSince' | 'accumulatedSeconds'>,
  nowMs: number,
): number {
  const running = timer.runningSince
    ? Math.floor((nowMs - Date.parse(timer.runningSince)) / 1000)
    : 0;
  return Math.max(0, timer.accumulatedSeconds + Math.max(0, running));
}

/** `HH:MM:SS`, or `2z 03:11:07` past a day so the bar cannot grow unbounded. */
export function formatElapsed(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const rest = seconds % 86_400;
  const h = Math.floor(rest / 3600);
  const m = Math.floor((rest % 3600) / 60);
  const s = rest % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
  return days > 0 ? `${days}z ${clock}` : clock;
}

/** The wall-clock time a timer was first started, as `HH:MM:SS`. */
export function formatStartedAt(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
